if (!customElements.get('gallery-watch')) {
  customElements.define('gallery-watch', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.link = this.querySelector('a');
      this.viewer = this.closest('slider-component');
      if (!this.link || !this.viewer) return;
      this.row = this.viewer.querySelector('.slider-buttons');
      this.abort = new AbortController();
      try { this.purchaseConfig = JSON.parse(this.querySelector('[data-watch-purchase]')?.textContent || '{}'); }
      catch (_) { this.purchaseConfig = {}; }
      const options = { signal: this.abort.signal };
      this.mobile = matchMedia('(max-width: 749px)');
      this.link.dataset.devices = this.dataset.devices || 'both';
      ['--gallery-watch-color', '--gallery-watch-size'].forEach(property => this.link.style.setProperty(property, this.style.getPropertyValue(property)));
      this.link.addEventListener('click', event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (!this.readSource()) return;
        event.preventDefault();
        if (this.dataset.action === 'scroll') {
          document.getElementById(this.data.target)?.scrollIntoView({ behavior: this.motion(), block: 'start' });
        } else this.open();
      }, options);
      this.onUpdate = () => this.update();
      this.mobile.addEventListener('change', this.onUpdate, options);
      document.addEventListener('shopify:section:load', this.onUpdate, options);
      document.addEventListener('shopify:section:unload', this.onUpdate, options);
      this.observer = new MutationObserver(records => {
        const source = node => node.nodeType === 1 && (node.matches('[data-gallery-video-source]') || node.querySelector('[data-gallery-video-source]'));
        if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(source))) this.update();
      });
      this.observer.observe(document.body, { subtree: true, childList: true });
      this.update();
    }

    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.observer?.disconnect();
      this.row?.classList.remove('gallery-controls--watch');
      this.close();
      if (this.link && this.link.parentElement !== this) this.append(this.link);
    }

    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }

    readSource() {
      const sources = [...document.querySelectorAll('[data-gallery-video-source]')];
      const source = this.dataset.source ? sources.find(node => node.dataset.galleryVideoSource === this.dataset.source) : sources[0];
      try {
        this.data = source ? JSON.parse(source.textContent) : null;
        return Array.isArray(this.data?.videos) && this.data.videos.some(video => this.videoURL(video));
      } catch (_) { this.data = null; return false; }
    }

    update() {
      const ready = this.readSource();
      this.link.hidden = !ready;
      if (!ready) { this.close(); return; }
      this.link.href = `#${this.data.target}`;
      if (this.dataset.action === 'modal') this.link.setAttribute('aria-haspopup', 'dialog');
      else this.link.removeAttribute('aria-haspopup');
      if (this.mobile.matches && this.row) {
        if (!this.row.classList.contains('gallery-controls--watch')) this.row.classList.add('gallery-controls--watch');
        if (this.link.parentElement !== this.row) this.row.append(this.link);
      } else {
        this.row?.classList.remove('gallery-controls--watch');
        if (this.link.parentElement !== this) this.append(this.link);
      }
    }

    videoURL(clip) {
      const mp4 = (clip.sources || []).filter(source => source.format === 'mp4' || source.mime_type === 'video/mp4');
      const sorted = mp4.sort((a, b) => (a.height || 0) - (b.height || 0));
      return (sorted.filter(source => (source.height || 0) <= 720).at(-1) || sorted[0])?.url || '';
    }

    open() {
      if (this.dialog?.open) return;
      this.clips = this.data.videos.filter(clip => this.videoURL(clip));
      if (!this.clips.length) return;
      this.index = 0;
      this.openPosition = { left: scrollX, top: scrollY };
      const dialog = document.createElement('dialog');
      dialog.className = 'gallery-video-modal';
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-label', this.data.title || 'See playtime in motion');
      const header = document.createElement('div'); header.className = 'gallery-video-modal__header';
      const title = document.createElement('h2'); title.className = 'gallery-video-modal__title'; title.textContent = this.data.title;
      const close = this.button(this.dataset.close || 'Close videos', '×'); close.className = 'gallery-video-modal__close';
      close.addEventListener('click', () => dialog.close());
      header.append(title, close);
      this.track = document.createElement('div'); this.track.className = 'gallery-video-modal__track';
      this.track.setAttribute('aria-label', this.data.title || 'Videos');
      this.players = this.clips.map((clip, index) => {
        const slide = document.createElement('div'); slide.className = 'gallery-video-modal__slide';
        slide.setAttribute('role', 'group'); slide.setAttribute('aria-label', `Video ${index + 1} of ${this.clips.length}`);
        const video = document.createElement('video'); video.controls = true; video.playsInline = true;
        video.preload = 'none'; video.loop = !!clip.loop; video.muted = this.dataset.autoplay === 'true' || !!clip.muted;
        video.setAttribute('aria-label', clip.description || `Video ${index + 1}`);
        if (clip.poster) video.poster = clip.poster;
        video.addEventListener('play', () => this.players.forEach(player => { if (player !== video) player.pause(); }));
        slide.append(video);
        if (clip.description) { const description = document.createElement('p'); description.className = 'gallery-video-modal__description'; description.textContent = clip.description; slide.append(description); }
        this.track.append(slide);
        return video;
      });
      const footer = document.createElement('div'); footer.className = 'gallery-video-modal__footer';
      this.previous = this.button(this.dataset.previous || 'Previous video', '‹');
      this.next = this.button(this.dataset.next || 'Next video', '›');
      this.counter = document.createElement('span'); this.counter.className = 'gallery-video-modal__count';
      this.counter.setAttribute('aria-live', 'polite');
      this.previous.addEventListener('click', () => this.go(this.index - 1));
      this.next.addEventListener('click', () => this.go(this.index + 1));
      footer.append(this.previous, this.counter, this.next);
      dialog.append(header, this.track, footer);
      this.addPurchaseRow(dialog);
      this.track.addEventListener('scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => {
          if (this.track?.clientWidth) this.activate(Math.round(this.track.scrollLeft / this.track.clientWidth));
        }, 120);
      }, { passive: true });
      dialog.addEventListener('close', () => this.close());
      dialog.addEventListener('click', event => { if (event.target === dialog) { const rect = dialog.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close(); } });
      this.dialog = dialog;
      document.body.append(dialog);
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      dialog.showModal();
      this.activate(0); close.focus({ preventScroll: true });
      window.scrollTo({ ...this.openPosition, behavior: 'instant' });
    }

    button(label, text) {
      const button = document.createElement('button'); button.type = 'button'; button.setAttribute('aria-label', label); button.textContent = text; return button;
    }

    addPurchaseRow(dialog) {
      const config = this.purchaseConfig;
      this.productInfo = document.getElementById(`ProductInfo-${config.section}`);
      this.mainAtc = document.getElementById(`ProductSubmitButton-${config.section}`);
      if ((!config.atc && !config.options) || !this.productInfo || !this.mainAtc) return;
      this.purchaseRow = document.createElement('div');
      this.purchaseRow.className = 'gallery-video-modal__purchase';
      dialog.classList.add('gallery-video-modal--purchase');
      Object.entries({ '--watch-atc-bg': config.background || '#2468b4', '--watch-atc-text': config.textColor || '#ffffff', '--watch-options-color': config.linkColor || '#2468b4', '--watch-atc-height': `${Number(config.height) || 56}px`, '--watch-atc-radius': `${Number(config.radius) || 8}px` }).forEach(([key, value]) => dialog.style.setProperty(key, value));
      if (config.options) {
        this.optionsAction = document.createElement('a');
        this.optionsAction.className = 'gallery-video-modal__options';
        this.optionsAction.href = `#${this.productInfo.id}`;
        this.optionsAction.addEventListener('click', event => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          const choices = this.productInfo.querySelector('.pets-aov-offers, variant-selects, variant-radios');
          if (!choices) return;
          event.preventDefault();
          this.close(false);
          choices.scrollIntoView({ behavior: this.motion(), block: 'center' });
          const selected = choices.querySelector('[role="tab"][aria-selected="true"]') || choices.querySelector('[role="radio"][aria-checked="true"]');
          selected?.focus({ preventScroll: true });
        });
        this.purchaseRow.append(this.optionsAction);
      }
      if (config.atc) {
        this.purchaseButton = this.button(config.atcLabel || 'Add to cart', '');
        this.purchaseButton.className = 'gallery-video-modal__atc';
        this.purchaseLabel = document.createElement('span'); this.purchaseLabel.className = 'gallery-video-modal__atc-label';
        this.purchaseSummary = document.createElement('span'); this.purchaseSummary.className = 'gallery-video-modal__atc-summary';
        this.purchaseButton.append(this.purchaseLabel, this.purchaseSummary);
        this.purchaseButton.addEventListener('click', () => {
          if (!this.mainAtc?.isConnected || this.mainAtc.disabled) return;
          this.close(false);
          // Delegate to the original button so AOV, validation and selected extras remain intact.
          this.mainAtc.click();
        });
        this.purchaseRow.append(this.purchaseButton);
      }
      dialog.append(this.purchaseRow);
      this.syncPurchase();
      this.purchaseObserver = new MutationObserver(() => this.syncPurchase());
      this.purchaseObserver.observe(this.productInfo, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'hidden', 'data-unavailable', 'aria-checked', 'data-selected'] });
    }

    syncPurchase() {
      if (!this.purchaseRow) return;
      const config = this.purchaseConfig;
      if (this.purchaseButton) {
        const unavailable = this.mainAtc.dataset.unavailable === 'true';
        this.purchaseButton.disabled = this.mainAtc.disabled;
        const label = unavailable ? this.mainAtc.querySelector('.main-atc__label__text')?.textContent.trim() || 'Unavailable' : config.atcLabel || 'Add to cart';
        if (this.purchaseLabel.textContent !== label) this.purchaseLabel.textContent = label;
        const mainSummary = this.mainAtc.querySelector('main-atc-variant-summary:not([hidden]), main-atc-bundle-summary:not([hidden])');
        const summary = config.offer && !unavailable ? mainSummary?.textContent.trim() || '' : '';
        if (this.purchaseSummary.textContent !== summary) this.purchaseSummary.textContent = summary;
        if (this.purchaseSummary.hidden !== !summary) this.purchaseSummary.hidden = !summary;
        this.purchaseButton.setAttribute('aria-label', [label, summary].filter(Boolean).join('. '));
      }
      if (this.optionsAction) {
        const offers = [...this.productInfo.querySelectorAll('.pets-aov-offers .Avada-VolumeItem__Container > .Avada-Volume__Item')];
        let lowest = null;
        if (config.currency && window.PetsUpsellOffers?.readMoney) offers.forEach(offer => {
          const text = offer.querySelector('.AOV-Offer__DiscountPrice')?.textContent.trim() || '';
          const cents = window.PetsUpsellOffers.readMoney(text, config.currency);
          if (text && typeof cents === 'number' && Number.isFinite(cents) && cents >= 0 && (!lowest || cents < lowest.cents)) lowest = { cents, text };
        });
        const wording = config.optionsLabel || 'Other options from [price] ›';
        const variants = this.productInfo.querySelector('variant-selects, variant-radios');
        const text = lowest && offers.length > 1 ? wording.replaceAll('[price]', lowest.text) : variants && !wording.includes('[price]') ? wording : '';
        if (this.optionsAction.textContent !== text) this.optionsAction.textContent = text;
        if (this.optionsAction.hidden !== !text) this.optionsAction.hidden = !text;
      }
      const visible = [...this.purchaseRow.children].filter(child => !child.hidden).length;
      this.purchaseRow.hidden = visible === 0;
      this.purchaseRow.dataset.layout = visible > 1 ? 'split' : 'single';
    }

    activate(index) {
      if (index < 0 || index >= this.clips.length) return;
      const newlyActive = this.index !== index || !this.players[index].getAttribute('src');
      this.index = index;
      this.players.forEach((player, i) => {
        player.parentElement.toggleAttribute('inert', i !== index);
        if (i !== index) player.pause();
        else if (!player.getAttribute('src')) player.src = this.videoURL(this.clips[i]);
      });
      const text = `${index + 1} / ${this.clips.length}`;
      if (this.counter.textContent !== text) this.counter.textContent = text;
      this.previous.disabled = index === 0; this.next.disabled = index === this.clips.length - 1;
      if (newlyActive && this.dataset.autoplay === 'true') {
        const player = this.players[index];
        player.muted = true;
        // Keep native controls usable if a browser or device blocks autoplay.
        player.play()?.catch(() => {});
      }
    }

    go(index) {
      if (index < 0 || index >= this.clips.length) return;
      this.activate(index);
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: this.motion() });
    }

    close(restoreFocus = true) {
      if (!this.dialog) return;
      const dialog = this.dialog; this.dialog = null;
      this.purchaseObserver?.disconnect();
      clearTimeout(this.scrollTimer);
      this.players?.forEach(video => { video.pause(); video.removeAttribute('src'); video.load(); });
      if (dialog.open) dialog.close();
      dialog.remove();
      document.documentElement.style.overflow = this.previousOverflow;
      window.scrollTo({ ...this.openPosition, behavior: 'instant' });
      if (restoreFocus && this.link?.isConnected) this.link.focus({ preventScroll: true });
      this.purchaseRow = null; this.optionsAction = null; this.purchaseButton = null;
    }
  });
}
